import { FunctionComponent } from "react";
import { format, isValid } from "date-fns";

import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { CustomColumnTypes } from "@/enum/list.enum";

import linkedinIcon from "@/assets/icons/linkedin.svg?url";

import googleNewsIcon from "@/assets/icons/googleNewsIcon.svg?url";

import { Icon } from "@/components";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { ColumnValue } from "../../../List/schema/columnValue";
import { ColumnConfig } from "../../../List/schema/columnConfig";
import { linkedinPostColumnUrl } from "../../../List/utils/linkedinPostColumnUrl";

export interface CustomTextColumnReadOnlyProps {
	columnValue?: ColumnValue;
}

export const CustomTextColumnReadOnly: FunctionComponent<CustomTextColumnReadOnlyProps> = ({
	columnValue,
}) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getValue = (columnValue: any) => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const cVal = columnValue?.value as any;

			let answer =
				cVal?.answer !== undefined && cVal?.answer !== null
					? typeof cVal?.answer === "object"
						? JSON.stringify(cVal?.answer)?.replaceAll("{", "").replaceAll("}", "")
						: cVal?.answer?.toString()
					: cVal?.title || cVal?.commentary || cVal || "";

			if (typeof answer === "object") {
				answer = JSON.stringify(answer)?.replaceAll("{", "").replaceAll("}", "");
			}

			return answer;
		} catch (e) {
			console.log(e);
		}
	};

	const getReasoning = (columnValue?: ColumnValue) => {
		if (columnValue?.answerExplanation) {
			return columnValue?.answerExplanation;
		}

		if (columnValue?.value?.reasoning) {
			return columnValue?.value?.reasoning;
		}

		return "";
	};

	const getTimestamp = (columnValue?: ColumnValue) => {
		try {
			if (columnValue?.value?.timestamp) {
				const date = new Date(+columnValue?.value?.timestamp);

				if (isValid(date)) {
					return format(date, "MMM d, yyyy");
				}
			}

			if (columnValue?.value?.postedAt) {
				const date = new Date(columnValue?.value?.postedAt);

				if (isValid(date)) {
					return format(date, "MMM d, yyyy");
				}
			}

			return "";
		} catch (e) {
			console.log(e);

			return "";
		}
	};

	const getIcon = (columnConfig: ColumnConfig, columnValue?: ColumnValue) => {
		let icon = null;

		const linkedinPostUrl =
			columnConfig.type === CustomColumnTypes.LINKEDIN_POST
				? linkedinPostColumnUrl(columnValue?.value, columnValue?.sourceUrl)
				: null;

		try {
			if (columnConfig.type === CustomColumnTypes.GOOGLE_NEWS && !!columnValue?.value) {
				icon = <img alt="googleNewsIcon" className="mr-1 w-[20px] h-[20px]" src={googleNewsIcon} />;
			}

			if (
				(columnConfig.type === CustomColumnTypes.AI_PROMPT ||
					columnConfig.type === CustomColumnTypes.JOB_CHANGE) &&
				!!columnValue?.value &&
				(columnValue?.value?.sourceUrl || columnValue?.sourceUrl)
			) {
				icon = <Icon className="mr-1 w-[16px] h-[16px]" icon={faExternalLink} />;
			} else if (columnConfig.type === CustomColumnTypes.AI_PROMPT && !!columnValue?.value) {
				return <></>;
			}

			if (columnConfig.type === CustomColumnTypes.LINKEDIN_POST && linkedinPostUrl) {
				icon = <img alt="Linkedin Icon" className="mr-1 w-[20px] h-[20px]" src={linkedinIcon} />;
			}

			if (icon === null) {
				<></>;
			}

			return (
				<div
					className="mr-1 min-w-[20px] min-h-[20px] flex items-center justify-center"
					onClick={(e) => {
						e.stopPropagation();
						if (columnConfig.type === CustomColumnTypes.GOOGLE_NEWS && !!columnValue?.value) {
							window.open(columnValue?.value?.newsUrl, "_blank");
						}

						if (linkedinPostUrl) {
							window.open(linkedinPostUrl, "_blank");
						}

						if (columnConfig.type === CustomColumnTypes.AI_PROMPT && !!columnValue?.value) {
							window.open(columnValue?.value?.sourceUrl || columnValue?.sourceUrl, "_blank");
						}
					}}
				>
					{icon}
				</div>
			);
		} catch (e) {
			return <></>;
		}
	};

	return (
		<Tooltip
			content={
				<div className="flex flex-col min-w-[300px] text-sm">
					<div>
						<span
							className="leading-6 truncate ..."
							style={{
								textWrap: "wrap",
							}}
						>
							{getValue(columnValue)}
							<br />
							<span className="text-xs text-gray-500">{getTimestamp(columnValue)}</span>
							<span className="text-xs text-gray-500 leading-1">{getReasoning(columnValue)}</span>
						</span>
					</div>
				</div>
			}
			disabled={!columnValue}
		>
			<div
				className={classNames(
					"h-[33px] min-w-[150px] cursor-pointer leading-tight flex items-center rounded p-1",
					"text-left  w-full",
					"cursor-pointer mr-1  max-w-[200px] text-ellipsis overflow-hidden"
				)}
			>
				{columnValue?.columnConfig ? getIcon(columnValue.columnConfig, columnValue) : <></>}
				<span className="truncate">{getValue(columnValue)}</span>
			</div>
		</Tooltip>
	);
};
